import { useState, useEffect } from "react";
import { useAbortedEffect, useHookForm } from "../../../../components/hooks";
import { useDispatch } from "react-redux";
import { setProgress, setToast } from "../../../../redux/slice/system";
import { RecordAPI } from "../../../../axios/api";
import moment from "moment";

import {
  FormCalendar,
  FormInputnumber,
  FormRadio,
  FormDropdown,
} from "../../../../components/formElement";
import { formatResData, checkFormUpdate } from "../../../../service/common";
import { checkValValid, rateHandler } from "../common";
import {
  option_period_time,
  option_warn_limit,
  errorRecordMsg,
} from "../../../../service/option";

import { classNames } from "primereact/utils";
import { Button } from "primereact/button";

export default function ModulePopRecordHeartbeatForm({
  prePopOption,
  closePrePop,
  openPrePop,
  setPreReload,
  memberid,
  moduleType,
  memberData,
}) {
  const dispatch = useDispatch();
  const [apiData, setApiData] = useState({});
  // 表單欄位預設值
  const defaultValues = {
    date: moment().format("YYYY/MM/DD"),
    type: null,
    systolic_blood_pressure: null,
    diastolic_blood_pressure: null,
    heart_rate: null,
    memberid: null,
  };
  const { handleSubmit, reset, yup, setSchema, errors, setting } = useHookForm({
    defaultValues,
  });

  useEffect(() => {
    setSchema(
      yup
        .object({
          memberid: yup
            .number()
            .test("isValidPass", "必填", (value) => {
              return moduleType === "back" && !memberid ? value : true;
            })
            .nullable(),
          date: yup.string().required("必填"),
          type: yup.number().required("必填"),
          systolic_blood_pressure: yup.number().required("必填"),
          diastolic_blood_pressure: yup.number().required("必填"),
          heart_rate: yup.number().required("必填"),
        })
        .required()
    );
  }, []);

  useAbortedEffect(
    (signal) => {
      if (!prePopOption.data) return;

      dispatch(setProgress(true));

      let params = { id: prePopOption.data.id };

      RecordAPI.getRecordHeartbeatBloodPressureDetails({
        data: params,
        options: { signal },
      }).then((result) => {
        let { Response, data, message } = result;

        if (Response === 1) {
          let temp = formatResData(defaultValues, data);
          setApiData(temp);
          reset(temp);
        } else {
          dispatch(
            setToast({
              severity: "error",
              summary: message,
              detail: "",
            })
          );
        }

        dispatch(setProgress(false));
      });
    },
    [prePopOption.data]
  );

  const onSubmit = (submitData) => {
    dispatch(setProgress(true));

    let isAdd = !prePopOption.data,
      jsonData = formatResData(defaultValues, submitData);
    if (jsonData.date)
      jsonData.date = moment(jsonData.date).format("YYYY-MM-DD");

    if (!checkFormUpdate(jsonData, prePopOption.data ?? {})) {
      dispatch(
        setToast({
          severity: "warn",
          summary: "無資料變更",
          detail: "",
        })
      );
      dispatch(setProgress(false));
      closePrePop({ type: "form" });
      return;
    }

    let promise,
      toast = "";
    if (isAdd) {
      // 新增
      promise = RecordAPI.setRecordHeartbeatBloodPressureData({
        data: {
          ...jsonData,
          memberid: submitData.memberid ?? memberid,
        },
      });
      toast = "新增";
    } else {
      // 修改
      if (jsonData.date === prePopOption.data.date) delete jsonData.date;
      promise = RecordAPI.updateRecordHeartbeatBloodPressureData({
        data: {
          ...jsonData,
          id: prePopOption.data.id,
        },
      });
      toast = "修改";
    }

    promise.then(async (result) => {
      let { Response, my_old_day_data, my_todays_data, message } = result;

      if (Response === 1) {
        await rateHandler({
          memberid: submitData.memberid ?? memberid,
          my_old_day_data,
          my_todays_data,
          old_date: isAdd ? null : prePopOption.data.date,
          now_date: submitData.date,
        });

        dispatch(
          setToast({
            severity: "success",
            summary: `${toast}成功`,
            detail: "",
          })
        );
        closePrePop({ type: "form" });
        setPreReload((state) => ({ ...state, list: true }));

        // 檢查資料異常值，跳出警示通知
        if (
          (jsonData.systolic_blood_pressure !==
            prePopOption.data?.systolic_blood_pressure &&
            checkValValid({
              warn_limit: option_warn_limit,
              field: "systolic_blood_pressure",
              val: jsonData.systolic_blood_pressure,
            })) ||
          (jsonData.diastolic_blood_pressure !==
            prePopOption.data?.diastolic_blood_pressure &&
            checkValValid({
              warn_limit: option_warn_limit,
              field: "diastolic_blood_pressure",
              val: jsonData.diastolic_blood_pressure,
            })) ||
          (jsonData.heart_rate !== prePopOption.data?.heart_rate &&
            checkValValid({
              warn_limit: option_warn_limit,
              field: "heart_rate",
              val: jsonData.heart_rate,
            }))
        ) {
          openPrePop({ type: "confirm", data: { msg: errorRecordMsg } });
        }
      } else {
        dispatch(
          setToast({
            severity: "error",
            summary: `${toast}失敗`,
            detail: message ?? "",
          })
        );
        dispatch(setProgress(false));
      }
    });
  };

  return (
    <>
      <div className="px-4">
        <form onSubmit={handleSubmit(onSubmit)} className="formgrid grid">
          {moduleType === "back" && !memberid && (
            <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
              <label
                htmlFor="memberid"
                className={classNames(
                  { "p-error": errors.memberid },
                  "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
                )}
              >
                <span className="text-red-400 mr-1">*</span>
                會員
              </label>
              <div className="col-12 sm:col-9">
                <FormDropdown
                  setting={setting}
                  data={{
                    name: "memberid",
                    options: memberData,
                    optionLabel: "name",
                    optionValue: "id",
                    filter: true,
                    filterBy: "name",
                    disabled: prePopOption.data,
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
            <label
              htmlFor="date"
              className={classNames(
                { "p-error": errors.date },
                "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
              )}
            >
              <span className="text-red-400 mr-1">*</span>
              日期
            </label>
            <div className="col-12 sm:col-9">
              <FormCalendar
                setting={setting}
                data={{
                  name: "date",
                  // disabled: true,
                }}
              />
            </div>
          </div>

          <div className="flex flex-column align-items-start sm:flex-row field col-12">
            <label
              htmlFor="type"
              className={classNames(
                { "p-error": errors.type },
                "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0 sm:mt-2"
              )}
            >
              <span className="text-red-400 mr-1">*</span>
              時段
            </label>
            <div className="flex flex-wrap col-12 sm:col-9">
              {option_period_time.map((item) => (
                <FormRadio
                  setting={setting}
                  data={{
                    name: "type",
                    label: item.label,
                    value: item.value,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
            <label
              htmlFor="systolic_blood_pressure"
              className={classNames(
                { "p-error": errors.systolic_blood_pressure },
                "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
              )}
            >
              <span className="text-red-400 mr-1">*</span>
              收縮壓
            </label>
            <div className="flex col-12 sm:col-9">
              <FormInputnumber
                className="w-full -mr-1px"
                setting={setting}
                data={{
                  name: "systolic_blood_pressure",
                }}
              />
              <div className="flex align-items-center justify-content-center w-6rem bg-gray-200 border-1 border-400">
                mmHg
              </div>
            </div>
          </div>

          <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
            <label
              htmlFor="diastolic_blood_pressure"
              className={classNames(
                { "p-error": errors.diastolic_blood_pressure },
                "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
              )}
            >
              <span className="text-red-400 mr-1">*</span>
              舒張壓
            </label>
            <div className="flex col-12 sm:col-9">
              <FormInputnumber
                className="w-full -mr-1px"
                setting={setting}
                data={{
                  name: "diastolic_blood_pressure",
                }}
              />
              <div className="flex align-items-center justify-content-center w-6rem bg-gray-200 border-1 border-400">
                mmHg
              </div>
            </div>
          </div>

          <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
            <label
              htmlFor="heart_rate"
              className={classNames(
                { "p-error": errors.heart_rate },
                "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
              )}
            >
              <span className="text-red-400 mr-1">*</span>
              心跳
            </label>
            <div className="flex col-12 sm:col-9">
              <FormInputnumber
                className="w-full -mr-1px"
                setting={setting}
                data={{
                  name: "heart_rate",
                }}
              />
              <div className="flex align-items-center justify-content-center w-6rem bg-gray-200 border-1 border-400">
                次/分
              </div>
            </div>
          </div>

          <div className="col-12 text-right">
            <Button
              type="button"
              label="取消"
              className="p-button-secondary p-button-outlined ml-auto px-3"
              onClick={() => closePrePop({ type: "form" })}
            />
            <Button type="submit" label="儲存" className="ml-2 mr-2 px-3" />
          </div>
        </form>
      </div>
    </>
  );
}
